import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { useTranslation } from "react-i18next";
import "./App.css";
import "./utils/setupAuth"; // Setup development authentication

// Import pages
import Login from "./pages/Login";
import AdminPanel from "./pages/AdminPanel";
import UserManagement from "./pages/UserManagement";
import Profile from "./pages/Profile";
import KanbanBoard from "./pages/KanbanBoard";
import NotFound from "./pages/NotFound";
import Wiki from "./pages/Wiki";
import Dashboard from "./pages/Dashboard";
import CandidatesList from "./pages/CandidatesList";
import CandidateDetail from "./pages/CandidateDetail";
import CandidateImportPage from "./pages/CandidateImportPage";
import JobsList from "./pages/JobsList";
import JobImportPage from "./pages/JobImportPage";
import JobDetail from "./pages/JobDetail";
import JobActivityHistory from "./pages/JobActivityHistory";
import CustomersList from "./pages/CustomersList";
import PipelineKanban from "./pages/PipelineKanban";
import ApplicationDetail from "./pages/ApplicationDetail";
import PublicCandidateSummary from "./pages/PublicCandidateSummary";
import PublicConsentForm from "./pages/PublicConsentForm";
import PublicWithdrawalForm from "./pages/PublicWithdrawalForm";
import Analytics from "./pages/Analytics";
import ReleaseNotesPage from "./pages/ReleaseNotesPage";
import GdprReport from "./pages/GdprReport";
import OnboardingRegister from "./pages/OnboardingRegister";
import OnboardingWelcome from "./pages/OnboardingWelcome";

// Import components
import Navbar from "./components/Navbar";
import FeedbackWidget from "./components/FeedbackWidget";
import WhatsNewModal from "./components/WhatsNewModal";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import { getCurrentUser } from "./utils/auth";
import { SUPPORTED_LANGUAGE_CODES, SupportedLanguageCode } from "./i18n";

// Syncs i18next language with the user's preferred_language from their profile.
// On mount, checks the user cookie for a language preference and applies it.
// Also listens for localStorage storage events to sync language across other tabs.
const LanguageSync: React.FC = () => {
  const { i18n } = useTranslation();
  useEffect(() => {
    const user = getCurrentUser();
    if (
      user?.preferred_language &&
      SUPPORTED_LANGUAGE_CODES.includes(
        user.preferred_language as SupportedLanguageCode,
      ) &&
      i18n.language !== user.preferred_language
    ) {
      i18n.changeLanguage(user.preferred_language);
    }
  }, [i18n]);

  // Listen for language changes via localStorage (cross-tab sync only; StorageEvent does not fire in the originating tab)
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (
        e.key === "i18nextLng" &&
        e.newValue &&
        SUPPORTED_LANGUAGE_CODES.includes(
          e.newValue as SupportedLanguageCode,
        ) &&
        i18n.language !== e.newValue
      ) {
        i18n.changeLanguage(e.newValue);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [i18n]);

  return null;
};

// Bridges the API interceptor (outside React tree) to React Router.
// When the interceptor detects a 401 after failed refresh, it dispatches
// an "auth-logout" event. This component listens and navigates to /login
// without a full page reload.
const AuthLogoutListener: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = () => {
      try {
        navigate("/login", { replace: true });
      } catch (error) {
        console.error("Failed to navigate after auth-logout:", error);
        window.location.href = "/login";
      }
    };
    window.addEventListener("auth-logout", handler);
    return () => window.removeEventListener("auth-logout", handler);
  }, [navigate]);
  return null;
};

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthLogoutListener />
        <LanguageSync />
        <div className="App">
          <Navbar />
          <FeedbackWidget />
          <WhatsNewModal />
          <main className="main-content">
            <ErrorBoundary>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route
                  path="/onboarding/register"
                  element={<OnboardingRegister />}
                />
                <Route
                  path="/onboarding/welcome"
                  element={
                    <ProtectedRoute requireSubscription={false}>
                      <OnboardingWelcome />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Navigate to="/dashboard" replace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute requireAdmin>
                      <AdminPanel />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute requireAdmin>
                      <UserManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/gdpr-report"
                  element={
                    <ProtectedRoute requireAdmin>
                      <GdprReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/candidates"
                  element={
                    <ProtectedRoute>
                      <CandidatesList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/candidates/import"
                  element={
                    <ProtectedRoute>
                      <CandidateImportPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/candidates/:id"
                  element={
                    <ProtectedRoute>
                      <CandidateDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/jobs"
                  element={
                    <ProtectedRoute>
                      <JobsList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/jobs/import"
                  element={
                    <ProtectedRoute>
                      <JobImportPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/jobs/:id/history"
                  element={
                    <ProtectedRoute>
                      <JobActivityHistory />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/jobs/:id"
                  element={
                    <ProtectedRoute>
                      <JobDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/applications/:id"
                  element={
                    <ProtectedRoute>
                      <ApplicationDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customers"
                  element={
                    <ProtectedRoute>
                      <CustomersList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/pipeline"
                  element={
                    <ProtectedRoute>
                      <PipelineKanban />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/kanban"
                  element={
                    <ProtectedRoute>
                      <KanbanBoard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/candidate/:code"
                  element={<PublicCandidateSummary />}
                />
                <Route path="/consent/:token" element={<PublicConsentForm />} />
                <Route
                  path="/consent/:token/withdraw"
                  element={<PublicWithdrawalForm />}
                />
                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute>
                      <Analytics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/release-notes"
                  element={
                    <ProtectedRoute>
                      <ReleaseNotesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/wiki"
                  element={
                    <ProtectedRoute>
                      <Wiki />
                    </ProtectedRoute>
                  }
                />
                <Route path="/404" element={<NotFound />} />
                <Route path="*" element={<Navigate to="/404" replace />} />
              </Routes>
            </ErrorBoundary>
          </main>
        </div>
      </Router>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default App;
